import { User } from "@/models/User";
import session from "@/services/SessionService";
import config from "@/config";
import { AxiosPromise } from "axios";
import { dniToNumber } from "@/filters/DniFilter";
import { SearchPagination, SearchFilters } from "./SearchService";
/**
 * Type for all available user filters
 */
export type UserSearch = {
  pagination: SearchPagination;
  filters: SearchFilters;
};
/** Type for user security payload */
export type SecurityPayload = {
  auto: boolean;
  password: string;
  send_email: boolean;
};

export type PermissionsPayload = {
  budget_id?: number;
  plant_id?: number;
  project_id?: number;
};

/**
 * New user payload
 */
export type UserPayload = {
  user: Partial<User>;
  permissions: PermissionsPayload[];
  security: Partial<SecurityPayload>;
};
/**
 * Response type for user data
 */
type UserGetResponse =
  | { users: User[]; pagination: SearchPagination }
  | undefined;
type UserFindResponse = User | undefined;

/**
 * Singleton class for fetching user data from the API
 */
class UserService {
  private endpoint: string = config.hostname + "/users/";
  /**
   * Fetch an user by ID
   *
   * @param {UserSearch} filters
   *
   */
  find(id: number): AxiosPromise<UserFindResponse> {
    let promise: AxiosPromise<UserFindResponse>;
    promise = session.requestWithAuth("get", this.endpoint + String(id));
    return promise;
  }
  /**
   * Fetch all users matching the filters provided
   *
   * @param {UserSearch} filters
   *
   */
  get(params: UserSearch): AxiosPromise<UserGetResponse> {
    let promise: AxiosPromise<UserGetResponse>;
    promise = session.requestWithAuth("get", this.endpoint, params);
    return promise;
  }
  /**
   * Saves or updates the user provided
   *
   * @param {User} user
   */
  save = (payload: UserPayload): AxiosPromise<User> => {
    let promise: AxiosPromise<User>;
    payload.user.dni = dniToNumber(payload.user.dni);
    if (payload.user.id) {
      promise = session.requestWithAuth(
        "put",
        this.endpoint + String(payload.user.id),
        undefined,
        payload
      );
    } else {
      promise = session.requestWithAuth(
        "post",
        this.endpoint,
        undefined,
        payload
      );
    }
    return promise;
  };
  /**
   * Deletes the provided user
   *
   * @param {User} user
   */
  delete = (users: User[] | User): AxiosPromise => {
    let promise: AxiosPromise;
    if (Array.isArray(users)) {
      promise = session.requestWithAuth("delete", this.endpoint, {
        ids: users.map(item => {
          return item.id;
        })
      });
    } else {
      promise = session.requestWithAuth(
        "delete",
        this.endpoint + String(users.id)
      );
    }
    return promise;
  };
}

let userService = new UserService();

export default userService;
